import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getPrivateInstance } from "../_privateInstance";
import type { ApiFieldError } from "../../components/shared";

export const CASH_GAME_CONFIG_QUERY_KEY_ROOT = [
  "poker",
  "configuration",
  "cashGame",
] as const;

export type CashGameCurrency = "cash" | "coin";
export type CashGameVisibility = "public" | "private";

/** Which of the four independent config records is being read / written
 *  (the store is keyed by currency × visibility — POKENG-142). */
export type CashGameConfigKey = {
  currency: CashGameCurrency;
  visibility: CashGameVisibility;
};

export function cashGameConfigQueryKey(key: CashGameConfigKey) {
  return [...CASH_GAME_CONFIG_QUERY_KEY_ROOT, key.currency, key.visibility];
}

/** Cash-game table config as edited by the PAM page (POKENG-141/191).
 *  `rakePercentage` is a whole percent (5 = 5%) — the API speaks in
 *  fractions (0.05) and the mappers convert at the boundary. */
export type CashGameConfig = {
  maxPlayers: number | null;
  smallBlind: number | null;
  bigBlind: number | null;
  buyInBbMin: number | null;
  buyInBbMax: number | null;
  minPlayers: number | null;
  gameStartDelay: number | null;
  handStartDelay: number | null;
  allowNickname: boolean;
  rakePercentage: number | null;
  rakeCap: number | null;
  noFlopNoDrop: boolean;
  isAvEnabled: boolean;
};

/** Raw snake_case `config` object served / accepted by the v2 API
 *  (POKENG-142, Digant's contract comment of Jul 21). */
export type CashGameConfigApi = {
  max_players: number;
  small_blind: number;
  big_blind: number;
  buy_in_bb_min: number;
  buy_in_bb_max: number;
  min_players: number;
  game_start_delay: number;
  hand_start_delay: number;
  allow_nickname: boolean;
  rake_percentage: number;
  rake_cap: number;
  no_flop_no_drop: boolean;
  is_av_enabled: boolean;
};

/** Config plus the audit metadata the v2 envelope carries alongside it. */
export type CashGameConfigSnapshot = {
  config: CashGameConfig;
  version: number | null;
  updatedBy: string | null;
  updatedAt: string | null;
};

/** Field-name mapping used both for request bodies and for pinning
 *  server-side validation errors back onto the matching form input. */
const API_FIELD_TO_FORM_FIELD: Record<string, keyof CashGameConfig> = {
  max_players: "maxPlayers",
  small_blind: "smallBlind",
  big_blind: "bigBlind",
  buy_in_bb_min: "buyInBbMin",
  buy_in_bb_max: "buyInBbMax",
  min_players: "minPlayers",
  game_start_delay: "gameStartDelay",
  hand_start_delay: "handStartDelay",
  allow_nickname: "allowNickname",
  rake_percentage: "rakePercentage",
  rake_cap: "rakeCap",
  no_flop_no_drop: "noFlopNoDrop",
  is_av_enabled: "isAvEnabled",
};

function toNumberOrNull(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

/** 0.05 * 100 is 5.000000000000001 in IEEE doubles — round the converted
 *  percent so the form never shows float dust. */
function fractionToPercent(fraction: number | null): number | null {
  return fraction === null ? null : Math.round(fraction * 100 * 1e6) / 1e6;
}

function percentToFraction(percent: number): number {
  return Math.round((percent / 100) * 1e8) / 1e8;
}

/** Map the snake_case API `config` onto the camelCase form model. Missing or
 *  malformed fields become null (toggles default off) so a bad payload never
 *  crashes the form. */
export function mapApiToCashGameConfig(
  api: Partial<CashGameConfigApi> | null | undefined,
): CashGameConfig {
  return {
    maxPlayers: toNumberOrNull(api?.max_players),
    smallBlind: toNumberOrNull(api?.small_blind),
    bigBlind: toNumberOrNull(api?.big_blind),
    buyInBbMin: toNumberOrNull(api?.buy_in_bb_min),
    buyInBbMax: toNumberOrNull(api?.buy_in_bb_max),
    minPlayers: toNumberOrNull(api?.min_players),
    gameStartDelay: toNumberOrNull(api?.game_start_delay),
    handStartDelay: toNumberOrNull(api?.hand_start_delay),
    allowNickname: api?.allow_nickname === true,
    rakePercentage: fractionToPercent(toNumberOrNull(api?.rake_percentage)),
    rakeCap: toNumberOrNull(api?.rake_cap),
    noFlopNoDrop: api?.no_flop_no_drop === true,
    isAvEnabled: api?.is_av_enabled === true,
  };
}

/** Build the full `config` object for the PUT body. Callers only save after
 *  client-side validation, so every numeric field is a number by then. */
export function mapCashGameConfigToApi(
  config: CashGameConfig,
): CashGameConfigApi {
  return {
    max_players: config.maxPlayers ?? 0,
    small_blind: config.smallBlind ?? 0,
    big_blind: config.bigBlind ?? 0,
    buy_in_bb_min: config.buyInBbMin ?? 0,
    buy_in_bb_max: config.buyInBbMax ?? 0,
    min_players: config.minPlayers ?? 0,
    game_start_delay: config.gameStartDelay ?? 0,
    hand_start_delay: config.handStartDelay ?? 0,
    allow_nickname: config.allowNickname,
    rake_percentage: percentToFraction(config.rakePercentage ?? 0),
    rake_cap: config.rakeCap ?? 0,
    no_flop_no_drop: config.noFlopNoDrop,
    is_av_enabled: config.isAvEnabled,
  };
}

type V2Envelope = {
  message?: string;
  success?: boolean;
  data?: {
    config?: Partial<CashGameConfigApi>;
    version?: number;
    updated_by?: string;
    updated_at?: string;
  };
};

/** Parse the v2 envelope (`data.config` + audit metadata). Tolerates a flat
 *  `data` object (no `config` nesting) in case the BE finalizes flat. */
function toSnapshot(body: V2Envelope | null | undefined): CashGameConfigSnapshot {
  const data = body?.data ?? (body as V2Envelope["data"]);
  const apiConfig =
    (data?.config as Partial<CashGameConfigApi> | undefined) ??
    (data as Partial<CashGameConfigApi> | undefined);
  return {
    config: mapApiToCashGameConfig(apiConfig),
    version: toNumberOrNull(data?.version),
    updatedBy: typeof data?.updated_by === "string" ? data.updated_by : null,
    updatedAt: typeof data?.updated_at === "string" ? data.updated_at : null,
  };
}

/** Error carrying per-field server validation messages mapped to form field
 *  names, so the page can pin them inline under the matching inputs. */
export class CashGameConfigSaveError extends Error {
  fieldErrors: ApiFieldError[];

  constructor(message: string, fieldErrors: ApiFieldError[] = []) {
    super(message);
    this.name = "CashGameConfigSaveError";
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Extract `{ field, message }` pairs from an API validation-error body.
 * Supports the two envelope shapes admin-panel services use:
 * `errors: { <field>: "msg" }` and `errors: [{ field, message }]`.
 * API field names are translated to form field names; unknown fields are
 * dropped (their messages still surface through the top-level message).
 */
export function extractFieldErrors(body: unknown): ApiFieldError[] {
  const errors = (body as { errors?: unknown } | null | undefined)?.errors;
  if (!errors) return [];

  const entries: Array<{ field: unknown; message: unknown }> = Array.isArray(
    errors,
  )
    ? (errors as Array<{ field?: unknown; message?: unknown }>).map((e) => ({
        field: e?.field,
        message: e?.message,
      }))
    : Object.entries(errors as Record<string, unknown>).map(
        ([field, message]) => ({ field, message }),
      );

  return entries.flatMap(({ field, message }) => {
    const formField =
      typeof field === "string" ? API_FIELD_TO_FORM_FIELD[field] : undefined;
    return formField && typeof message === "string"
      ? [{ field: formField, message }]
      : [];
  });
}

export async function fetchCashGameConfig(
  key: CashGameConfigKey,
): Promise<CashGameConfigSnapshot> {
  const { data: body } = await getPrivateInstance().get(
    API_ROUTES.POKER_CASH_GAME_CONFIG,
    { params: { currency: key.currency, visibility: key.visibility } },
  );
  if (body?.success === false) {
    throw new Error(body?.message || AppStrings.POKER_CASH_GAME_LOAD_ERROR);
  }
  return toSnapshot(body);
}

export function useCashGameConfig(
  key: CashGameConfigKey,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cashGameConfigQueryKey(key),
    queryFn: () => fetchCashGameConfig(key),
    staleTime: 60_000,
    // Gated by the pokerCashGameConfigV2 feature flag — no v2 call is made
    // while the flag is off or still resolving.
    enabled,
  });
}

export type SaveCashGameConfigVariables = {
  key: CashGameConfigKey;
  config: CashGameConfig;
};

export async function saveCashGameConfig({
  key,
  config,
}: SaveCashGameConfigVariables): Promise<CashGameConfigSnapshot> {
  try {
    const { data: body } = await getPrivateInstance().put(
      API_ROUTES.POKER_CASH_GAME_CONFIG,
      {
        currency: key.currency,
        visibility: key.visibility,
        config: mapCashGameConfigToApi(config),
      },
    );
    if (body?.success === false) {
      throw new CashGameConfigSaveError(
        body?.message || AppStrings.POKER_CASH_GAME_SAVE_ERROR,
        extractFieldErrors(body),
      );
    }
    return toSnapshot(body);
  } catch (error) {
    if (error instanceof CashGameConfigSaveError) throw error;
    // Axios 4xx/5xx: surface the server's field errors when present.
    const responseBody = (
      error as { response?: { data?: { message?: string } } }
    )?.response?.data;
    if (responseBody) {
      throw new CashGameConfigSaveError(
        responseBody.message || AppStrings.POKER_CASH_GAME_SAVE_ERROR,
        extractFieldErrors(responseBody),
      );
    }
    throw error;
  }
}

export function useUpdateCashGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveCashGameConfig,
    onSuccess: (saved, variables) => {
      // Seed the cache with what the server actually persisted so a reload
      // (or another consumer) reflects the saved values without a refetch.
      queryClient.setQueryData(cashGameConfigQueryKey(variables.key), saved);
    },
  });
}
